import {Inject, Injectable} from '@angular/core';
import {MessageService} from '../../core/service/message.service';
import {
  EsriGraphic,
  EsriGraphicsLayer,
  EsriSimpleFillSymbol,
  EsriSimpleLineSymbol,
  EsriSimpleMarkerSymbol,
  EsriSketchViewModel,
} from '../../config/esri.module';
import {MainMapService} from './main-map.service';
import {ConfigService} from '../../config/config.service';
import {Subject} from 'rxjs';
import {CONSTANTS} from '../../config/constants.module';

@Injectable({
  providedIn: 'root',
})
export class SketchGeometryService {
  public newFeatureGeometry: __esri.Geometry;
  public onUpdatedGeometrySubject: Subject<boolean>;
  public onUpdatedGeometryCompleteSubject: Subject<boolean>;
  public sketchStateCompleted = false;

  private _rotatedAngle = 0;

  constructor(
    private _messageService: MessageService,
    private _mainMapService: MainMapService,
    private _configService: ConfigService,
    @Inject(CONSTANTS) private _CONST: any
  ) {
    this.onUpdatedGeometrySubject = new Subject();
    this.onUpdatedGeometryCompleteSubject = new Subject();
  }

  /**
   * @function setupSketch
   * @description Create SketchViewModel and wire up event update listeners
   */
  public setupSketch(
    sketchLyr: __esri.GraphicsLayer,
    enabledScaling: boolean,
    enabledRotation: boolean,
    enabledToggleTool: boolean,
    esriGeometryType: string
  ) {
    const sketchProperties: __esri.SketchViewModelProperties = {
      view: this._mainMapService.mapView,
      layer: sketchLyr,
    };
    const esriSketchViewModel = new EsriSketchViewModel(sketchProperties);

    esriSketchViewModel.defaultUpdateOptions.enableScaling = enabledScaling;
    esriSketchViewModel.defaultUpdateOptions.enableRotation = enabledRotation;
    esriSketchViewModel.defaultUpdateOptions.toggleToolOnClick = enabledToggleTool;

    if (esriGeometryType === this._CONST.ESRI_GEOMETRY_TYPE.POINT) {
      esriSketchViewModel.defaultUpdateOptions.tool = this._CONST.TOOL_TYPE.MOVE;
    } else if (
      (esriGeometryType === this._CONST.ESRI_GEOMETRY_TYPE.POLYGON ||
        esriGeometryType === this._CONST.ESRI_GEOMETRY_TYPE.POLYLINE) &&
      sketchLyr.id !== 'print'
    ) {
      esriSketchViewModel.defaultUpdateOptions.tool = this._CONST.TOOL_TYPE.RESHAPE;
    } else if (sketchLyr.id === 'print') {
      esriSketchViewModel.defaultUpdateOptions.tool = this._CONST.TOOL_TYPE.TRANSFORM;
    }
    // set symbology for updating feature
    this.setSketchViewModelSymbols(esriSketchViewModel);

    const self = this;
    esriSketchViewModel.on('update', evt => {
      self.onGraphicUpdate(evt);
    });
    return esriSketchViewModel;
  }

  /**
   * @function rotatedAngleAsDEC
   * @description getter rotated angle on graphic
   */
  get rotatedAngleAsDEC(): number {
    return this._rotatedAngle;
  }

  /**
   * @function setRotatedAngleAsDEC
   * @description setter rotated angle on graphic
   */
  public setRotatedAngleAsDEC(rotatedValue: number) {
    this._rotatedAngle = rotatedValue;
  }

  /**
   * @function createGraphicsLayer
   * @description create new graphiclayer
   * @return esri graphiclayer
   */
  public createGraphicsLayer(graphicLyrId: string): __esri.GraphicsLayer {
    const graphicsLayer = new EsriGraphicsLayer();
    graphicsLayer.id = graphicLyrId;
    return graphicsLayer;
  }

  /**
   * @function createEditingGraphic
   * @description create new graphic
   * @return esri graphic
   */
  public createEditingGraphic(geometry: __esri.Geometry): __esri.Graphic {
    const symbol: __esri.SymbolUnion = this.createEditingSymbol(geometry.type);

    const graphicProperties: __esri.GraphicProperties = {
      geometry: geometry,
      symbol: symbol,
    };
    return new EsriGraphic(graphicProperties);
  }

  /* Private Methods
  ======================= */

  /**
   * @function onGraphicUpdate
   * @description graphic updating event listener
   */
  private onGraphicUpdate(evt) {
    try {
      if (evt.toolEventInfo) {
        const toolType = evt.toolEventInfo.type;
        if (toolType === this._CONST.TOOL_EVENT_TYPE.ROTATE_STOP) {
          const rotatedAngle = this.rotatedAngleAsDEC + evt.toolEventInfo.angle;
          this.setRotatedAngleAsDEC(rotatedAngle);
        } else if ((toolType === this._CONST.TOOL_EVENT_TYPE.MOVE_START || toolType === this._CONST.TOOL_EVENT_TYPE.MOVE)
          && evt.graphics[0].layer.id !== this._CONST.SKETCH_GEOMETRY_SERVICE.PRINT_GRAPHIC_LAYER_ID) {
          evt.target.cancel();
        } else {
          this.newFeatureGeometry = evt.graphics[0].geometry;
        }
        // Notify geometry edited
        this.onUpdatedGeometrySubject.next(true);
      }

      if (evt.state === this._CONST.TOOL_EVENT_TYPE.COMPLETE) {
        // TODO do it better
        this.sketchStateCompleted = true;
        this.onUpdatedGeometryCompleteSubject.next(true);
      }

    } catch (error) {
      throw Error('onEditingGraphic: ' + error);
    }

  }

  /**
   * @function createSymbol
   * @description create esri simple fill symbol
   * @return symbol
   */
  private createSymbol(): __esri.SimpleFillSymbol {
    return new EsriSimpleFillSymbol(this._configService.mainMapConfig.printGraphic.symbols.polygon);
  }

  /**
   * @function createSymbol
   * @description create esri simple fill symbol
   * @return symbol
   */
  private createEditingSymbol(geomtryType: string): __esri.SymbolUnion {
    switch (geomtryType) {
      case this._CONST.ESRI_GEOMETRY_TYPE.POINT:
        return new EsriSimpleMarkerSymbol(this._configService.mainMapConfig.edit.symbols.point);
      case this._CONST.ESRI_GEOMETRY_TYPE.POLYLINE:
        return new EsriSimpleLineSymbol(this._configService.mainMapConfig.edit.symbols.polyline);
      case this._CONST.ESRI_GEOMETRY_TYPE.POLYGON:
        return new EsriSimpleFillSymbol(this._configService.mainMapConfig.edit.symbols.polygon);
      default:
        throw new Error('not supported');
    }
  }

  /**
   * @function setSketchViewModelSymbols
   * @description set symbology for updating feature and activeted symbol
   */
  private setSketchViewModelSymbols(sketchViewModel: __esri.SketchViewModel) {
    // sketchViewModel.polygonSymbol = this.createEditingSymbol('polygon');
    sketchViewModel['updatePolygonSymbol'] = this.createEditingSymbol(
      this._CONST.ESRI_GEOMETRY_TYPE.POLYGON
    );
    sketchViewModel['activeFillSymbol'] = this.createEditingSymbol(
      this._CONST.ESRI_GEOMETRY_TYPE.POLYGON
    ) as __esri.SimpleFillSymbol;
    // polyline symbols
    sketchViewModel['updatePolylineSymbol'] = this.createEditingSymbol(
      this._CONST.ESRI_GEOMETRY_TYPE.POLYLINE
    );
    sketchViewModel['activeLineSymbol'] = this.createEditingSymbol(
      this._CONST.ESRI_GEOMETRY_TYPE.POLYLINE
    );
    // point
    sketchViewModel['activePointSymbol'] = this.createEditingSymbol(
      this._CONST.ESRI_GEOMETRY_TYPE.POINT
    );
    sketchViewModel['updatePointSymbol'] = this.createEditingSymbol(
      this._CONST.ESRI_GEOMETRY_TYPE.POINT
    );
  }
}
